import React, { useState } from 'react';
import { X } from 'lucide-react';
import { commercialsData } from '../mockCommercials';
import { mockData } from '../mock';

const HeroPortfolio = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [visibleCount, setVisibleCount] = useState(9);

  const categories = [
    { name: 'All', description: 'All Work' },
    { name: 'Commercials', description: 'Brand Films' },
    { name: 'Short Films and Web Show', description: 'Narrative Content' },
    { name: 'Celebrities', description: 'Celebrity Campaigns' },
    { name: 'Fashion & Lifestyle', description: 'Premium Brands' },
    { name: 'F&B', description: 'Food & Beverage' }
  ];

  const getProjects = () => {
    if (activeCategory === 'All') {
      // Return all commercials + films
      return [...commercialsData, ...mockData.featuredFilms];
    }
    if (activeCategory === 'Short Films and Web Show') {
      return mockData.featuredFilms;
    }
    if (activeCategory === 'Commercials') {
      return commercialsData;
    }
    if (activeCategory === 'Celebrities') {
      // Filter for celebrity campaigns
      return commercialsData.filter(c => 
        c.brand.includes('Kareena') || 
        c.brand.includes('Tisca') || 
        c.brand.includes('Shibani')
      );
    }
    if (activeCategory === 'Fashion & Lifestyle') {
      // Jaquar and Singleton Social
      return commercialsData.filter(c => 
        c.brand.includes('Jaquar') || 
        c.brand.includes('Singleton')
      );
    }
    if (activeCategory === 'F&B') {
      // Moi Soi and iD Foods
      return commercialsData.filter(c => 
        c.brand.includes('Moi Soi') || 
        c.brand.includes('iD') || 
        c.brand.includes('ID')
      );
    }
    return commercialsData;
  };

  const projects = getProjects();
  const visibleProjects = projects.slice(0, visibleCount);
  const hasMore = visibleCount < projects.length;

  const loadMore = () => {
    setVisibleCount(prev => Math.min(prev + 9, projects.length));
  };

  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-light text-gray-800 mb-8">MY PORTFOLIO</h2>
          
          {/* Category Sidebar */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => {
                    setActiveCategory(category.name);
                    setVisibleCount(9);
                  }}
                  className={`text-left py-2 px-4 text-sm transition-all ${
                    activeCategory === category.name
                      ? 'text-gray-900 font-medium underline'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Project Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {visibleProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              className="group cursor-pointer"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video overflow-hidden bg-gray-100 mb-4">
                <img
                  src={project.thumbnail}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all"></div>
              </div>

              {/* Project title */}
              <h3 className="text-lg font-light text-gray-800 group-hover:text-gray-600 transition-colors">
                {project.title}
              </h3>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="text-center mt-12">
            <button 
              onClick={loadMore}
              className="text-gray-600 hover:text-gray-900 font-light text-sm transition-colors underline"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 p-6">
          <button
            onClick={() => setSelectedProject(null)}
            className="absolute top-8 right-8 text-gray-600 hover:text-gray-900 transition-colors z-50"
          >
            <X size={32} />
          </button>

          <div className="w-full max-w-6xl">
            <div className="aspect-video w-full bg-black">
              <iframe
                src={
                  selectedProject.vimeoId
                    ? `https://player.vimeo.com/video/${selectedProject.vimeoId}?autoplay=1`
                    : selectedProject.youtubeId
                    ? `https://www.youtube.com/embed/${selectedProject.youtubeId}?autoplay=1`
                    : `${selectedProject.embedUrl}?autoplay=1`
                }
                className="w-full h-full"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
            <div className="mt-6">
              <h3 className="text-2xl font-light text-gray-800 mb-2">{selectedProject.title}</h3>
              <p className="text-gray-600 font-light">
                {selectedProject.brand || selectedProject.platform}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroPortfolio;
